import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { getAdminSession } from '../api/admin.js';
import {
  completeNewPassword,
  restoreSession,
  signIn,
  signOut,
  type AuthTokens,
  type PasswordChallenge,
} from './cognito.js';

interface AuthState {
  status: 'loading' | 'anonymous' | 'authenticated' | 'new-password';
  tokens: AuthTokens | undefined;
  challenge: PasswordChallenge | undefined;
  email: string | undefined;
  subject: string | undefined;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  finishNewPassword: (password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const initialState: AuthState = {
  status: 'loading',
  tokens: undefined,
  challenge: undefined,
  email: undefined,
  subject: undefined,
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(initialState);

  useEffect(() => {
    let cancelled = false;
    restoreSession()
      .then(async (tokens) => {
        if (cancelled) {
          return;
        }
        if (!tokens) {
          setState({ ...initialState, status: 'anonymous' });
          return;
        }
        const session = await getAdminSession(tokens.idToken);
        if (cancelled) {
          return;
        }
        setState({
          status: 'authenticated',
          tokens,
          challenge: undefined,
          email: session.email,
          subject: session.subject,
        });
      })
      .catch(() => {
        if (!cancelled) {
          signOut();
          setState({ ...initialState, status: 'anonymous' });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      login: async (email, password) => {
        const result = await signIn(email, password);
        if ('kind' in result) {
          setState({
            status: 'new-password',
            tokens: undefined,
            challenge: result,
            email,
            subject: undefined,
          });
          return;
        }
        const session = await getAdminSession(result.idToken);
        setState({
          status: 'authenticated',
          tokens: result,
          challenge: undefined,
          email: session.email ?? email,
          subject: session.subject,
        });
      },
      finishNewPassword: async (password) => {
        if (!state.challenge) {
          throw new Error('No password challenge is pending.');
        }
        const tokens = await completeNewPassword(state.challenge.user, password);
        const session = await getAdminSession(tokens.idToken);
        setState({
          status: 'authenticated',
          tokens,
          challenge: undefined,
          email: session.email ?? state.email,
          subject: session.subject,
        });
      },
      logout: () => {
        signOut();
        setState({ ...initialState, status: 'anonymous' });
      },
    }),
    [state],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return value;
}
